import {
  ButtonItem,
  ButtonOptions,
  Form as DXForm,
  FormRef,
  GroupItem,
  RequiredRule,
  SimpleItem,
  Tab,
  TabbedItem,
  TabPanelOptions,
} from 'devextreme-react/form'
import { DumpEditorFormProps } from '@/routes/dumps/DumpEditor.types'
import { useCallback, useMemo, useRef, useState } from 'react'
import xmlFormatter from 'xml-formatter'
import './DumpEditor.form.scss'
import { ClickEvent } from 'devextreme/ui/button'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import notify from 'devextreme/ui/notify'
import LoadingPanel from '@/components/loading-panel/LoadingPanel'
import { customButton, onInitializedHtmlEditor, onValueChangedHtmlEditor, toolbarHtmlEditor } from '@/routes/utils'
import { useScreenSize } from '@/themes/media-query'
import { FieldDataChangedEvent } from 'devextreme/ui/form'
import { useRevalidator } from 'react-router-dom'
import { ArraySourceComposer } from '@/auth/api/connector'
import DumpEditorEventsGridMemoed from '@/routes/dumps/DumpEditor.events'
import { StatoTicket, TipologiaTicket } from '@/routes/tickets/TicketEditor.enums'
import { email_aziendale, sede, ticket } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { DropDownButton, Popup } from 'devextreme-react'
import { TipologiaDump } from '@/routes/dumps/Dump.enums'
import { PopupRef } from 'devextreme-react/cjs/popup'
import DumpEditorTicketsGridMemoed from '@/routes/dumps/DumpEditor.tickets'
import { createTicketWithApi } from '@/routes/tickets/ticketWrite'
import { downloadDump } from '@/routes/dumps/utils'

const DumpEditorForm = (props: DumpEditorFormProps) => {
  const { dump, stati, tipologie, isRoleAllowed, creating, fetcher } = props
  const currentScreenSize = useScreenSize()
  const revalidator = useRevalidator()
  const [loadingPanelVisible, setLoadingPanelVisible] = useState<boolean>(false)
  const formRef = useRef<FormRef>(null)

  const { client, service } = useQsAdminApiManager()

  const currentTipologie = useMemo(() => {
    return dump?.tipologie
  }, [dump])

  const xmlCerdata = useCallback(() => {
    let returnValue: string = ''
    if (dump?.cerdata) {
      const wrappedXml = `<__wrapper>${dump?.cerdata}</__wrapper>`
      try {
        const parser = new DOMParser()
        const xmlDoc = parser.parseFromString(wrappedXml, 'text/xml')
        const parserError = xmlDoc.querySelector('parsererror')
        if (parserError) {
          console.error('XML parsing error')
          returnValue = dump?.cerdata
        } else {
          returnValue = xmlFormatter(wrappedXml)
            .replace(/^<__wrapper>\s*/, '')
            // eslint-disable-next-line sonarjs/slow-regex
            .replace(/\s*<\/__wrapper>$/, '')
        }
      } catch (error) {
        console.log('xmlFormatter error', error)
        returnValue = dump?.cerdata
      }
    }
    return returnValue
  }, [dump])

  const sendCrash = async (dumpTypeId: number, files?: string[]) => {
    try {
      setLoadingPanelVisible(true)
      const response = await client.post(
        'api/dump/sendcrashnotification',
        {
          dumpId: dump?.ded_Dis,
          dumpTypeId: dumpTypeId,
          files: files,
        },
        {
          headers: { 'Content-Type': 'application/json' },
        },
      )
      notify(`Notifica crash inviata correttamente.`, 'success', 3000)
    } catch (error) {
      notify(`Errore invio notifica crash: ${error}`, 'error', 3000)
    } finally {
      setLoadingPanelVisible(false)
    }
  }

  const popupFileMancanteRef = useRef<PopupRef>(null)
  const formPopupFileMancanteRef = useRef<FormRef>(null)
  const fileMancanteObject: { fileName: string } = { fileName: '' }

  const popupErroreSegmentoRef = useRef<PopupRef>(null)
  const formPopupErroreSegmentoRef = useRef<FormRef>(null)
  const [erroreSegmentoObject, setErroreSegmentoObject] = useState<{ fileNames: string[] }>({ fileNames: [''] })
  const [filesErroreSegmentoObject, setFilesErroreSegmentoObject] = useState<string[]>()

  const getFiles = useCallback(() => {
    const files: string[] = []
    for (const file of erroreSegmentoObject.fileNames) {
      files.push(file)
    }
    return files
  }, [erroreSegmentoObject.fileNames])

  return (
    <>
      <div className="data-part border dump-form">
        {creating !== undefined && (
          <LoadingPanel
            visible={!(fetcher?.state === 'idle' && revalidator.state === 'idle' && !loadingPanelVisible)}
          />
        )}
        <DXForm
          id={'dump_form'}
          formData={dump}
          onFieldDataChanged={(e: FieldDataChangedEvent) => {
            if (
              formRef.current?.instance().option().isDirty !== null &&
              formRef.current?.instance().option().isDirty !== undefined
            ) {
              console.log('is dirty', formRef.current?.instance().option().isDirty)
              formRef.current
                .instance()
                .getButton('btn-save')
                ?.option('disabled', !formRef.current?.instance().option().isDirty)
              formRef.current
                .instance()
                .getButton('btn-cancel')
                ?.option('disabled', !formRef.current?.instance().option().isDirty)
            }
          }}
          labelLocation={'top'}
          ref={formRef}
          readOnly={true}
        >
          {creating === undefined ? (
            <GroupItem>
              <TabbedItem>
                <TabPanelOptions
                  animationEnabled={true}
                  swipeEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall}
                  deferRendering={true}
                  scrollingEnabled={true}
                  defaultSelectedIndex={0}
                />
                <Tab title="Stack Trace">
                  <SimpleItem
                    dataField="stack_trace"
                    label={{ visible: false }}
                    editorType="dxTextArea"
                    editorOptions={{ height: 800 }}
                    cssClass="custom-textarea"
                  ></SimpleItem>
                </Tab>
                <Tab title="Cerdata">
                  <SimpleItem
                    // dataField="cerdata"
                    label={{ visible: false }}
                    editorType="dxTextArea"
                    editorOptions={{ height: 800, value: xmlCerdata() }}
                    cssClass="custom-textarea"
                  ></SimpleItem>
                </Tab>
                <Tab title="Log">
                  <SimpleItem
                    dataField="log"
                    label={{ visible: false }}
                    editorType="dxTextArea"
                    editorOptions={{ height: 800 }}
                    cssClass="custom-textarea"
                  ></SimpleItem>
                </Tab>
                <Tab title="Note">
                  <SimpleItem
                    dataField="note"
                    label={{ visible: false }}
                    editorType="dxTextArea"
                    editorOptions={{ height: 800 }}
                  ></SimpleItem>
                </Tab>
              </TabbedItem>
            </GroupItem>
          ) : (
            <>
              <GroupItem colCountByScreen={{ xs: 1, sm: 2, md: 2, lg: 2 }}>
                <GroupItem colCount={1}>
                  <GroupItem colCountByScreen={{ xs: 1, sm: 1, md: 1, lg: 4 }}>
                    <SimpleItem
                      dataField="data_ricezione"
                      editorOptions={{
                        displayFormat: 'dd/MM/yyyy HH:mm',
                        readOnly: true,
                      }}
                      editorType="dxDateBox"
                      label={{ text: 'Data Ricezione Dump' }}
                    ></SimpleItem>
                    <SimpleItem
                      dataField="data"
                      editorOptions={{
                        displayFormat: 'dd/MM/yyyy HH:mm',
                        readOnly: true,
                      }}
                      editorType="dxDateBox"
                      label={{ text: 'Data Dump' }}
                    ></SimpleItem>
                    <SimpleItem
                      dataField="email.email"
                      label={{ text: 'Inviato da' }}
                      editorType="dxTextBox"
                    ></SimpleItem>
                    <SimpleItem
                      dataField="licenza.sede.azienda.nome"
                      label={{ text: 'Azienda' }}
                      editorType="dxTextBox"
                      editorOptions={{
                        buttons: [
                          customButton(
                            'open',
                            (e: ClickEvent) => {
                              if (dump?.licenza) window.open(`/clients/${dump.licenza.sede?.azienda?.id}`, '_blank')
                              else notify(`Deve essere presente un' azienda.`, 'warning', 3000)
                            },
                            !dump?.licenza,
                          ),
                        ],
                      }}
                    />
                  </GroupItem>
                  <GroupItem colCountByScreen={{ xs: 1, sm: 1, md: 1, lg: 4 }}>
                    <SimpleItem
                      dataField="licenza.chiave"
                      label={{ text: 'Licenza' }}
                      editorType="dxTextBox"
                      editorOptions={{
                        buttons: [
                          customButton(
                            'open',
                            (e: ClickEvent) => {
                              if (dump?.licenza) window.open(`/software/qs/${dump.licenza.id}`, '_blank')
                              else notify(`Deve essere presente una licenza.`, 'warning', 3000)
                            },
                            !dump?.licenza,
                          ),
                        ],
                      }}
                    ></SimpleItem>
                    <SimpleItem
                      dataField="piattaforma"
                      editorType="dxTextBox"
                      label={{ text: 'Piattaforma' }}
                    ></SimpleItem>
                    <SimpleItem
                      dataField="piattaforma_versione"
                      editorType="dxTextBox"
                      label={{ text: 'Piattaforma Versione' }}
                    ></SimpleItem>
                    <SimpleItem
                      dataField="service_pack_update"
                      editorType="dxTextBox"
                      label={{ text: 'Service Pack Update' }}
                    ></SimpleItem>
                  </GroupItem>
                  <GroupItem colCountByScreen={{ xs: 1, sm: 1, md: 1, lg: 4 }}>
                    <SimpleItem
                      dataField="app_version"
                      editorType="dxTextBox"
                      label={{ text: 'App Version' }}
                    ></SimpleItem>
                    <SimpleItem
                      dataField="app_hotfix"
                      editorType="dxTextBox"
                      label={{ text: 'App Hotfix' }}
                    ></SimpleItem>
                    <SimpleItem
                      dataField="common_version"
                      editorType="dxTextBox"
                      label={{ text: 'Common Version' }}
                    ></SimpleItem>
                    <SimpleItem
                      dataField="common_hotfix"
                      editorType="dxTextBox"
                      label={{ text: 'Common Hotfix' }}
                    ></SimpleItem>
                  </GroupItem>
                  <GroupItem colCountByScreen={{ xs: 1, sm: 1, md: 1, lg: 5 }}>
                    <SimpleItem
                      dataField="session_uptime"
                      editorType="dxNumberBox"
                      label={{
                        text: 'Session Uptime',
                      }}
                      editorOptions={{
                        format: (number: number) => {
                          const text = number.toString()
                          return text + ' sec'
                        },
                      }}
                    ></SimpleItem>
                    <SimpleItem dataField="gdi" editorType="dxNumberBox" label={{ text: 'GDI' }}></SimpleItem>
                    <SimpleItem dataField="gdi_peak" editorType="dxNumberBox" label={{ text: 'GDI Peak' }}></SimpleItem>
                    <SimpleItem dataField="user_gdi" editorType="dxNumberBox" label={{ text: 'User GDI' }}></SimpleItem>
                    <SimpleItem
                      dataField="user_gdi_peak"
                      editorType="dxNumberBox"
                      label={{ text: 'User GDI Peak' }}
                    ></SimpleItem>
                  </GroupItem>
                  <GroupItem colCountByScreen={{ xs: 1, sm: 1, md: 1, lg: 2 }}>
                    <SimpleItem
                      dataField="stato"
                      label={{ text: 'Stato' }}
                      editorType="dxLookup"
                      editorOptions={{
                        dataSource: ArraySourceComposer('id', stati),
                        placeholder: 'Seleziona lo stato...',
                        displayExpr: 'nome',
                        searchEnabled: true,
                        dropDownCentered: true,
                        dropDownOptions: {
                          showTitle: true,
                          title: 'Selezionare lo stato',
                          hideOnOutsideClick: true,
                        },
                        readOnly: false,
                      }}
                    ></SimpleItem>
                    <SimpleItem
                      dataField="tipologie"
                      editorType="dxTagBox"
                      label={{ text: 'Tipologie' }}
                      editorOptions={{
                        dataSource: ArraySourceComposer('id', tipologie),
                        displayExpr: 'nome',
                        placeholder: 'Seleziona tipologie...',
                        applyValueMode: 'instantly',
                        showSelectionControls: true,
                        searchEnabled: true,
                        readOnly: false,
                      }}
                    ></SimpleItem>
                  </GroupItem>
                </GroupItem>
                <GroupItem colCount={1}>
                  <TabbedItem>
                    <TabPanelOptions
                      animationEnabled={true}
                      swipeEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall}
                      deferRendering={true}
                      scrollingEnabled={true}
                      defaultSelectedIndex={0}
                    />
                    <Tab title="Note">
                      <SimpleItem
                        dataField={'note'}
                        editorType={'dxTextArea'}
                        label={{ visible: false }}
                        editorOptions={{
                          height: '290',
                        }}
                      ></SimpleItem>
                    </Tab>
                    <Tab title="Note Tecnico">
                      <SimpleItem
                        dataField="note_tecnico"
                        label={{ visible: false }}
                        editorType="dxHtmlEditor"
                        editorOptions={{
                          stylingMode: 'filled',
                          valueType: 'html',
                          toolbar: toolbarHtmlEditor(currentScreenSize),
                          mediaResizing: {
                            enabled: true,
                          },
                          imageUpload: {
                            tabs: ['file', 'url'],
                            fileUploadMode: 'base64',
                          },
                          height: 290,
                          onValueChanged: onValueChangedHtmlEditor,
                          onInitialized: onInitializedHtmlEditor,
                          readOnly: false,
                        }}
                      />
                    </Tab>
                    <Tab title="Eventi">
                      <DumpEditorEventsGridMemoed dump={dump}></DumpEditorEventsGridMemoed>
                    </Tab>
                    <Tab title="Ticket">
                      <DumpEditorTicketsGridMemoed dump={dump}></DumpEditorTicketsGridMemoed>
                    </Tab>
                  </TabbedItem>
                </GroupItem>
              </GroupItem>
              <GroupItem colCountByScreen={{ xs: 1, sm: 3, md: 3, lg: 3 }}>
                <SimpleItem
                  dataField="stack_trace"
                  editorType="dxTextArea"
                  editorOptions={{ height: 400 }}
                  cssClass="custom-textarea"
                ></SimpleItem>
                <SimpleItem
                  dataField="cerdata"
                  label={{ text: 'Cerdata' }}
                  editorType="dxTextArea"
                  editorOptions={{ height: 400, value: xmlCerdata() }}
                  cssClass="custom-textarea"
                ></SimpleItem>
                <SimpleItem
                  dataField="log"
                  editorType="dxTextArea"
                  editorOptions={{ height: 400 }}
                  cssClass="custom-textarea"
                ></SimpleItem>
              </GroupItem>
              <GroupItem visible={creating !== undefined && isRoleAllowed} cssClass="last-group">
                <GroupItem cssClass="buttons-group" colCountByScreen={{ xs: 1, sm: 1, md: 1, lg: 5 }}>
                  <SimpleItem>
                    <DropDownButton
                      id="ddbNotificaCrash-dump"
                      text="NOTIFICA"
                      icon="email"
                      disabled={dump?.tipologie?.length === 0}
                      useItemTextAsTitle={false}
                      items={[
                        {
                          icon: 'fas fa-question',
                          text: `Info. non sufficienti`,
                          onClick: (e: ClickEvent) => sendCrash(TipologiaDump['INFO NON SUFFICIENTI']),
                          visible:
                            currentTipologie?.find(
                              (tipologia) => tipologia.id === TipologiaDump['INFO NON SUFFICIENTI'],
                            ) !== undefined,
                        },
                        {
                          icon: 'fas fa-circle-exclamation',
                          text: `Saturazione GDI`,
                          onClick: (e: ClickEvent) => sendCrash(TipologiaDump['SATURAZIONE GDI']),
                          visible:
                            currentTipologie?.find((tipologia) => tipologia.id === TipologiaDump['SATURAZIONE GDI']) !==
                            undefined,
                        },
                        {
                          icon: 'fas fa-xmark',
                          text: `Errore Segmento`,
                          onClick: (e: ClickEvent) => {
                            popupErroreSegmentoRef.current?.instance().show()
                          },
                          visible:
                            currentTipologie?.find((tipologia) => tipologia.id === TipologiaDump['ERRORE SEGMENTO']) !==
                            undefined,
                        },
                        {
                          icon: 'fas fa-file-circle-question',
                          text: `File Mancante`,
                          onClick: (e: ClickEvent) => {
                            popupFileMancanteRef.current?.instance().show()
                          },
                          visible:
                            currentTipologie?.find((tipologia) => tipologia.id === TipologiaDump['FILE MANCANTE']) !==
                            undefined,
                        },
                        {
                          icon: 'fas fa-triangle-exclamation',
                          text: `Saturazione Memoria`,
                          onClick: (e: ClickEvent) => sendCrash(TipologiaDump['SATURAZIONE MEMORIA']),
                          visible:
                            currentTipologie?.find(
                              (tipologia) => tipologia.id === TipologiaDump['SATURAZIONE MEMORIA'],
                            ) !== undefined,
                        },
                        {
                          icon: 'fas fa-arrows-spin',
                          text: `Dipendenza Ciclica`,
                          onClick: (e: ClickEvent) => sendCrash(TipologiaDump['DIPENDENZA CICLICA']),
                          visible:
                            currentTipologie?.find(
                              (tipologia) => tipologia.id === TipologiaDump['DIPENDENZA CICLICA'],
                            ) !== undefined,
                        },
                        {
                          icon: 'fas fa-file-circle-xmark',
                          text: `Template Inventor Corrotto`,
                          onClick: (e: ClickEvent) => sendCrash(TipologiaDump['TEMPLATE INVENTOR CORROTTO']),
                          visible:
                            currentTipologie?.find(
                              (tipologia) => tipologia.id === TipologiaDump['TEMPLATE INVENTOR CORROTTO'],
                            ) !== undefined,
                        },
                      ]}
                      dropDownOptions={{ width: '250' }}
                      width={'150px'}
                    />
                  </SimpleItem>
                  <ButtonItem name="btn-downloadzip">
                    <ButtonOptions
                      text="TICKET"
                      icon="fas fa-ticket"
                      useSubmitBehavior={false}
                      width={'150px'}
                      onClick={async (e: ClickEvent) => {
                        try {
                          setLoadingPanelVisible(true)
                          const stato = await service.stato_ticket(StatoTicket.APERTO).query()
                          const tipologia = await service.tipologia_ticket(TipologiaTicket.ASSISTENZA_TECNICA).query()
                          const dumpLink: string = `<a href="/dumps/${dump?.id}">${dump?.ded_Dis}</a> `
                          const emailDump: email_aziendale | null | undefined = dump?.email
                          const sedeDump: sede | null | undefined = dump?.licenza?.sede
                          const ticket: ticket = {
                            id: 0,
                            ded_Dis: '',
                            ded_RootFam: '',
                            ded_Id: 0,
                            contattato: false,
                            attesa: false,
                            urgente: false,
                            notifica_chiusura: false,
                            rating: 0,
                            rating_note: null,
                            apertura: new Date().toISOString(),
                            chiusura: null,
                            descrizione: `Ticket creato da dump ${dumpLink}`,
                            soluzione: null,
                            leg_tag1: null,
                            leg_tag2: null,
                            durata: 0,
                            leg_contatto: emailDump?.email ?? '',
                            leg_n_chiamate: 0,
                            leg_email_contatto: emailDump?.email ?? '',
                            leg_phone_contatto: null,
                            assegnato: null,
                            stato: stato.data,
                            attachmentsFolderUrl: '',
                            sede: sedeDump,
                            tipologia_ticket: tipologia.data,
                            dumps: dump ? [dump] : [],
                            customer_id: null,
                          }
                          const response = await createTicketWithApi(client, ticket)
                          window.open(`/tickets/${response.data.ticketRecordId}`, '_blank')
                        } catch (error: unknown) {
                          notify(`Errore creazione ticket: ${error}`, 'error', 3000)
                        } finally {
                          setLoadingPanelVisible(false)
                        }
                      }}
                    />
                  </ButtonItem>
                  <ButtonItem name="btn-downloadzip">
                    <ButtonOptions
                      text="DOWNLOAD"
                      icon="download"
                      useSubmitBehavior={false}
                      width={'150px'}
                      onClick={async (e: ClickEvent) => {
                        if (!dump) return

                        try {
                          setLoadingPanelVisible(true)
                          await downloadDump(client, dump)
                        } catch (error: unknown) {
                          notify(`Errore download dump. Dettagli : ${error}`, 'error', 2000)
                        } finally {
                          setLoadingPanelVisible(false)
                        }
                      }}
                    />
                  </ButtonItem>
                  <ButtonItem name="btn-save">
                    <ButtonOptions
                      disabled={!formRef.current?.instance().option().isDirty}
                      width="150px"
                      text={'Salva'}
                      icon={'save'}
                      useSubmitBehavior={false}
                      onClick={async () => {
                        const validationResult = formRef.current?.instance().validate()
                        if (!validationResult?.isValid) return
                        if (dump) {
                          fetcher?.submit(JSON.stringify(dump), {
                            method: creating ? 'POST' : 'PATCH',
                            encType: 'application/json',
                          })
                        } else {
                          console.log('no data to post')
                          console.log(dump)
                        }
                      }}
                    />
                  </ButtonItem>
                  <ButtonItem name="btn-cancel">
                    <ButtonOptions
                      disabled={!formRef.current?.instance().option().isDirty}
                      width="150px"
                      text="Annulla"
                      icon="undo"
                      onClick={() => {
                        revalidator.revalidate()
                      }}
                    />
                  </ButtonItem>
                </GroupItem>
              </GroupItem>
            </>
          )}
        </DXForm>
        <Popup
          ref={popupFileMancanteRef}
          id="file_mancante_popup"
          title="File Mancante"
          hideOnOutsideClick={false}
          showCloseButton={true}
          showTitle={true}
          container=".dx-viewport"
          resizeEnabled={false}
          width={currentScreenSize.isLarge || currentScreenSize.isMedium ? 400 : currentScreenSize.isXSmall ? 350 : 700}
          height={currentScreenSize.isSmall ? '350' : 'auto'}
        >
          <DXForm
            formData={fileMancanteObject}
            id={`file_mancante_popup_form`}
            labelLocation={'top'}
            showValidationSummary={true}
            validationGroup={`tfile_mancante_popup_formValidation`}
            ref={formPopupFileMancanteRef}
            onFieldDataChanged={(e: FieldDataChangedEvent) => {
              if (
                formPopupFileMancanteRef.current?.instance().option().isDirty !== null &&
                formPopupFileMancanteRef.current?.instance().option().isDirty !== undefined
              ) {
                formPopupFileMancanteRef.current
                  .instance()
                  .getButton('btn-save')
                  ?.option('disabled', !formPopupFileMancanteRef.current?.instance().option().isDirty)
              }
            }}
          >
            <GroupItem colCount={1}>
              <SimpleItem dataField="fileName">
                <RequiredRule message="File obbligatorio"></RequiredRule>
              </SimpleItem>
            </GroupItem>
            <GroupItem cssClass="last-group">
              <GroupItem cssClass="buttons-group" colCount={1}>
                <ButtonItem name="btn-save">
                  <ButtonOptions
                    disabled={!formPopupFileMancanteRef.current?.instance().option().isDirty}
                    icon={'email'}
                    text={'NOTIFICA'}
                    width="150px"
                    onClick={(e: ClickEvent) => {
                      const validationResult = formPopupFileMancanteRef.current?.instance().validate()
                      if (!validationResult?.isValid) return
                      popupFileMancanteRef.current?.instance().hide()
                      sendCrash(TipologiaDump['FILE MANCANTE'], [fileMancanteObject.fileName])
                    }}
                  />
                </ButtonItem>
              </GroupItem>
            </GroupItem>
          </DXForm>
        </Popup>
        <Popup
          ref={popupErroreSegmentoRef}
          id="erroreSegmento_popup"
          title="Errore Segmento"
          hideOnOutsideClick={false}
          showCloseButton={true}
          showTitle={true}
          container=".dx-viewport"
          resizeEnabled={false}
          width={currentScreenSize.isLarge || currentScreenSize.isMedium ? 400 : currentScreenSize.isXSmall ? 350 : 700}
          height={currentScreenSize.isSmall ? '350' : 'auto'}
        >
          <DXForm
            formData={erroreSegmentoObject}
            id={`erroreSegmento_popup_form`}
            labelLocation={'top'}
            showValidationSummary={true}
            validationGroup={`erroreSegmento_popup_formValidation`}
            ref={formPopupErroreSegmentoRef}
            onFieldDataChanged={(e: FieldDataChangedEvent) => {
              if (
                formPopupErroreSegmentoRef.current?.instance().option().isDirty !== null &&
                formPopupErroreSegmentoRef.current?.instance().option().isDirty !== undefined
              ) {
                formPopupErroreSegmentoRef.current
                  .instance()
                  .getButton('btn-save')
                  ?.option('disabled', !formPopupErroreSegmentoRef.current?.instance().option().isDirty)
              }
            }}
          >
            <GroupItem colCount={1}>
              {getFiles()?.map((string, index) => (
                <SimpleItem
                  key={`File${index}`}
                  label={{ text: `File ${index + 1}` }}
                  dataField={`fileNames[${index}]`}
                  editorOptions={{
                    buttons: [
                      {
                        name: 'trash',
                        location: 'after',
                        options: {
                          visible: index > 0,
                          stylingMode: 'text',
                          icon: 'trash',
                          onClick: () => {
                            erroreSegmentoObject.fileNames.splice(index, 1)
                            setFilesErroreSegmentoObject(getFiles())
                            // formNewContactRef.current?.forceUpdate()
                          },
                        },
                      },
                    ],
                  }}
                >
                  <RequiredRule message="File obbligatorio" />
                </SimpleItem>
              ))}
              <ButtonItem name="addEmailBtn" verticalAlignment={'bottom'} colSpan={2}>
                <ButtonOptions
                  text={'Aggiungi File'}
                  icon={'add'}
                  type="default"
                  stylingMode="text"
                  onClick={function (e: ClickEvent) {
                    erroreSegmentoObject.fileNames.push('')
                    setFilesErroreSegmentoObject(getFiles())
                    // formNewContactRef.current?.forceUpdate()
                  }}
                />
              </ButtonItem>
            </GroupItem>
            <GroupItem cssClass="last-group">
              <GroupItem cssClass="buttons-group" colCount={1}>
                <ButtonItem name="btn-save">
                  <ButtonOptions
                    disabled={!formPopupErroreSegmentoRef.current?.instance().option().isDirty}
                    icon={'email'}
                    text={'NOTIFICA'}
                    width="150px"
                    onClick={(e: ClickEvent) => {
                      const validationResult = formPopupErroreSegmentoRef.current?.instance().validate()
                      if (!validationResult?.isValid) return
                      popupErroreSegmentoRef.current?.instance().hide()
                      setErroreSegmentoObject({ fileNames: [''] })
                      sendCrash(TipologiaDump['ERRORE SEGMENTO'], erroreSegmentoObject.fileNames)
                    }}
                  />
                </ButtonItem>
              </GroupItem>
            </GroupItem>
          </DXForm>
        </Popup>
      </div>
    </>
  )
}

export default DumpEditorForm
